import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { dashboardAPI } from '../services/api';
import { reportLoadError } from '../utils/notify';
import { parseLocalDate } from '../utils/dateUtils';
import DashboardFinance from '../components/DashboardFinance';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { Building2, Coins, CreditCard, Receipt, RefreshCw, TrendingUp, Trophy, User, UserCheck, Users, Wallet, History } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { applyChartJsTheme, chartColors, withAlpha } from '../utils/chartTheme';
import './Dashboard.css';

// Register ChartJS components
applyChartJsTheme(ChartJS);
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  // Same rule as the backend's requirePermission('finance', 'view').
  const financePermission = Array.isArray(user?.permissions)
    ? user.permissions.find((p) => p.resource === 'finance') || {}
    : {};
  const canViewFinance = user?.role === 'super_admin' || financePermission.can_view === true;
  const { t, locale } = useLanguage();
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [payrollTrend, setPayrollTrend] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [
        statsRes,
        summaryRes,
        trendRes,
        deptRes,
        payrollRes,
        performersRes,
        activitiesRes
      ] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getSummary(),
        dashboardAPI.getAttendanceTrend(),
        dashboardAPI.getDepartmentStats(),
        dashboardAPI.getPayrollTrend(),
        dashboardAPI.getTopPerformers(5),
        dashboardAPI.getRecentActivity() // ← CORRECTO
      ]);

      setStats(statsRes.data.stats);
      setSummary(summaryRes.data.summary);
      setAttendanceTrend(trendRes.data.trend || []);
      setDepartmentStats(deptRes.data.departments || []);
      setPayrollTrend(payrollRes.data.trend || []);
      setTopPerformers(performersRes.data.performers || []);
      setActivities(activitiesRes.data.activity || []); // ← CORRECTO
    } catch (error) {
      console.error('Error loading dashboard:', error);
      reportLoadError(error, t('Error loading dashboard'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  // Chart configurations
  const colors = chartColors();
  const attendanceChartData = {
    labels: attendanceTrend.map(d => parseLocalDate(d.date)?.toLocaleDateString(locale, { month: 'short', day: 'numeric' })),
    datasets: [
      {
        label: t('Total Attendance'),
        data: attendanceTrend.map(d => d.total_attendance || 0),
        borderColor: colors.primary,
        backgroundColor: withAlpha(colors.primary, 0.12),
        tension: 0.4,
        fill: true
      },
      {
        label: t('Unique Employees'),
        data: attendanceTrend.map(d => d.unique_employees || 0),
        borderColor: colors.dark,
        backgroundColor: withAlpha(colors.dark, 0.06),
        tension: 0.4,
        fill: true
      }
    ]
  };

  const departmentChartData = {
    labels: departmentStats.map(d => d.department || t('Unknown')),
    datasets: [
      {
        label: t('Employees'),
        data: departmentStats.map(d => d.employee_count || 0),
        backgroundColor: colors.primary,
        borderRadius: 3,
        maxBarThickness: 48
      }
    ]
  };

  // One line per currency: the server no longer adds SRD and USD together.
  const payrollMonths = [...new Set(payrollTrend.map(d => d.month))];
  const payrollCurrencies = [...new Set(payrollTrend.map(d => d.currency || 'SRD'))];
  const currencyColors = colors.series.map((c) => [c, withAlpha(c, 0.1)]);
  const payrollChartData = {
    labels: payrollMonths,
    datasets: payrollCurrencies.map((currency, i) => ({
      label: `${t('Total Amount')} (${currency})`,
      data: payrollMonths.map(month => {
        const row = payrollTrend.find(d => d.month === month && (d.currency || 'SRD') === currency);
        return row ? parseFloat(row.total_amount || 0) : 0;
      }),
      borderColor: currencyColors[i % currencyColors.length][0],
      backgroundColor: currencyColors[i % currencyColors.length][1],
      tension: 0.4,
      fill: true
    }))
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top'
      }
    },
    scales: {
      x: {
        grid: { display: false }
      },
      y: {
        beginAtZero: true,
        grid: { color: colors.grid }
      }
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('Loading dashboard...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Welcome back, {name}!', { name: user?.first_name || user?.username })}</h1>
          <p className="page-sub">{t("Here's what's happening with your business today.")}</p>
        </div>
        <div className="page-actions">
          <button type="button" onClick={loadDashboardData} className="btn btn--secondary">
            <RefreshCw aria-hidden="true" /> {t('Refresh')}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="kpi-grid">
        <div className="kpi">
          <div className="kpi__label"><span className="kpi__icon"><Users aria-hidden="true" /></span>{t('Total Employees')}</div>
          <p className="kpi__value">{stats?.employees?.total || 0}</p>
          <div className="kpi__foot">{t('{count} active', { count: stats?.employees?.active || 0 })}</div>
        </div>

        <div className="kpi">
          <div className="kpi__label"><span className="kpi__icon"><UserCheck aria-hidden="true" /></span>{t('Present Today')}</div>
          <p className="kpi__value">{summary?.today_attendance || 0}</p>
          <div className="kpi__foot">{t('{count} currently working', { count: stats?.attendance?.currently_working || 0 })}</div>
        </div>

        <div className="kpi">
          <div className="kpi__label"><span className="kpi__icon"><Receipt aria-hidden="true" /></span>{t('Payroll This Month')}</div>
          <p className="kpi__value">{stats?.payroll?.total_records || 0}</p>
          <div className="kpi__foot">{t('{count} pending', { count: summary?.pending_payroll || 0 })}</div>
        </div>

        <div className="kpi">
          <div className="kpi__label"><span className="kpi__icon"><Coins aria-hidden="true" /></span>{t('Total Payroll')}</div>
          <p className="kpi__value">
            {(stats?.payroll?.by_currency?.length ? stats.payroll.by_currency : [{ currency: 'SRD', total_payroll: 0 }]).map(c => (
              <span key={c.currency} className="stat-currency-line">
                {c.currency} {c.total_payroll.toLocaleString(locale)}
              </span>
            ))}
          </p>
          <div className="kpi__foot">{t('This month')}</div>
        </div>
      </div>

      {canViewFinance && <DashboardFinance />}

      {/* Charts Row 1 */}
      {attendanceTrend.length > 0 && (
        <div className="charts-row">
          <section className="card">
            <div className="card__header"><h2 className="card__title"><TrendingUp aria-hidden="true" />{t('Attendance Trend (Last 7 Days)')}</h2></div>
            <div className="card__body">
              <div className="chart-container">
                <Line data={attendanceChartData} options={chartOptions} />
              </div>
            </div>
          </section>

          {departmentStats.length > 0 && (
            <section className="card">
              <div className="card__header"><h2 className="card__title"><Building2 aria-hidden="true" />{t('Department Statistics')}</h2></div>
              <div className="card__body">
                <div className="chart-container">
                  <Bar data={departmentChartData} options={chartOptions} />
                </div>
              </div>
            </section>
          )}
        </div>
      )}

      {/* Charts Row 2 */}
      {payrollTrend.length > 0 && (
        <div className="charts-row">
          <section className="card wide">
            <div className="card__header"><h2 className="card__title"><Wallet aria-hidden="true" />{t('Payroll Trend (Last 6 Months)')}</h2></div>
            <div className="card__body">
              <div className="chart-container">
                <Line data={payrollChartData} options={chartOptions} />
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Bottom Section */}
      <div className="bottom-section">
        {/* Top Performers */}
        {topPerformers.length > 0 && (
          <section className="card">
            <div className="card__header"><h2 className="card__title"><Trophy aria-hidden="true" />{t('Top Performers')}</h2></div>
            <ul className="dash-list">
              {topPerformers.map((performer, index) => (
                <li key={performer.id}>
                  <div className="performer-rank">#{index + 1}</div>
                  <div className="performer-info">
                    <strong>{performer.name}</strong>
                    <span className="performer-position">{performer.position || 'N/A'}</span>
                  </div>
                  <div className="performer-stats">
                    <StatusBadge variant="success">
                      {t('{count} days', { count: performer.attendance_count })}
                    </StatusBadge>
                    <StatusBadge variant="info">
                      {t('{hours}h avg', { hours: parseFloat(performer.avg_hours || 0).toFixed(1) })}
                    </StatusBadge>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Recent Activities */}
        {activities && activities.length > 0 && (
          <section className="card">
            <div className="card__header"><h2 className="card__title"><History aria-hidden="true" />{t('Recent Activities')}</h2></div>
            <ul className="dash-list">
              {activities.slice(0, 8).map((activity, index) => (
                <li key={index}>
                  <div className="dash-activity__icon">
                    {activity.type === 'attendance' && <User aria-hidden="true" />}
                    {activity.type === 'expense' && <CreditCard aria-hidden="true" />}
                    {activity.type === 'payroll' && <Wallet aria-hidden="true" />}
                  </div>
                  <div className="dash-activity__body">
                    <p className="dash-activity__text">
                      {activity.description}
                    </p>
                    <span className="dash-activity__time">
                      {new Date(activity.created_at).toLocaleString(locale)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
